import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

// Trading Cards tab of /admin/reports — option 1c ("sparkline"), recency first:
// a stats + 30-day sparkline card, then every athlete's NEWEST card grouped by
// day, then By template / AI funnel folded into one-line rows.
//
// Days are America/Chicago calendar days, on the server (window, series,
// `day` on each row) and here (labels, times). The zone comes back from the
// API as `timezone`; every date below is formatted IN that zone rather than
// the viewer's, so "Today" on this page always matches the server's grouping.
//
// Styles: AdminReportsPage.css (`.tcr-*`). Colours and sizes are classes, not
// inline var(--adm-*) — jsdom drops those, and the rendered-markup check
// depends on them. The only inline styles are literal values (bar heights,
// placeholder fills).

export const TC_WINDOWS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const DEFAULT_TZ = 'America/Chicago';
const NEW_MS = 3 * 60 * 60 * 1000;
const EXPANDED_KEY = 'pz.reports.tc.expanded';

// Placeholder fills, only when a card has no rendered composite yet.
const TEMPLATE_COLORS = {
  classic: '#0D2133',
  neon: '#3F6B53',
  vintage: '#A47630',
  chrome: '#4A5867',
  retro: '#7A3B2E',
  ice: '#2F5D7C',
};
const templateColor = (t) => TEMPLATE_COLORS[(t || '').toLowerCase()] || '#0D2133';
const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown');

const initials = (name) => (name || '')
  .split(/\s+/)
  .filter(Boolean)
  .map((w) => w[0])
  .join('')
  .slice(0, 2)
  .toUpperCase();

// ---------------------------------------------------------------- dates

// Server timestamps here are timestamptz (carry an offset); tolerate a bare
// one anyway by reading it as UTC, as the rest of this page does.
const parseIso = (iso) => {
  const hasTz = /([zZ])|([+-]\d{2}:?\d{2})$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`);
};

// 'YYYY-MM-DD' for an instant, in `tz`.
const isoDayIn = (date, tz) => new Intl.DateTimeFormat('en-CA', {
  timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
}).format(date);

const addDays = (isoDay, n) => {
  const d = new Date(`${isoDay}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// Format a calendar date ('YYYY-MM-DD'). Noon UTC + timeZone UTC keeps the
// date itself fixed whatever the viewer's zone.
const fmtDay = (isoDay, opts) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', ...opts })
  .format(new Date(`${isoDay}T12:00:00Z`));

const fmtTime = (date, tz) => new Intl.DateTimeFormat('en-US', {
  timeZone: tz, hour: 'numeric', minute: '2-digit',
}).format(date);

// "Today" / "Yesterday" / "Wed Oct 22"
export const dayLabel = (isoDay, today) => {
  if (isoDay === today) return 'Today';
  if (isoDay === addDays(today, -1)) return 'Yesterday';
  return fmtDay(isoDay, { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '');
};

// "14 min ago" / "9 hr ago" today; "Yesterday 8:12 PM"; "Wed 7:30 PM" this
// week; "Oct 3, 7:30 PM" beyond that.
export const whenLabel = (iso, day, today, tz, nowMs = Date.now()) => {
  const at = parseIso(iso);
  const mins = Math.max(0, Math.floor((nowMs - at.getTime()) / 60000));
  if (day === today) {
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    return `${Math.floor(mins / 60)} hr ago`;
  }
  const time = fmtTime(at, tz);
  if (day === addDays(today, -1)) return `Yesterday ${time}`;
  if (day >= addDays(today, -6)) return `${fmtDay(day, { weekday: 'short' })} ${time}`;
  return `${fmtDay(day, { month: 'short', day: 'numeric' })}, ${time}`;
};

// ------------------------------------------------------------- expanded

const readExpanded = () => {
  try {
    const v = JSON.parse(window.localStorage.getItem(EXPANDED_KEY) || '{}');
    return { templates: !!v.templates, funnel: !!v.funnel };
  } catch (e) {
    return { templates: false, funnel: false };
  }
};

const writeExpanded = (v) => {
  try {
    window.localStorage.setItem(EXPANDED_KEY, JSON.stringify(v));
  } catch (e) {
    // storage blocked (private window etc.) — the toggle still works this visit
  }
};

// ------------------------------------------------------------ component

export default function TradingCardsReport({ days = 7 }) {
  const { API_URL } = useAuth();
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(readExpanded);

  const config = useCallback((params) => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    params,
  }), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get(`${API_URL}/admin/reports/trading-cards`, config({ days }))
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setRecent(res.data?.recent_athletes || []);
        setCursor(res.data?.recent_cursor || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || 'Failed to load the trading cards report.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [API_URL, config, days]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API_URL}/admin/reports/trading-cards`, config({ days, before: cursor }));
      setRecent((prev) => {
        const seen = new Set(prev.map((r) => r.user_id));
        return prev.concat((res.data?.recent_athletes || []).filter((r) => !seen.has(r.user_id)));
      });
      setCursor(res.data?.recent_cursor || null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load earlier cards.');
    } finally {
      setLoadingMore(false);
    }
  };

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      writeExpanded(next);
      return next;
    });
  };

  const tz = data?.timezone || DEFAULT_TZ;
  const today = isoDayIn(new Date(), tz);

  const groups = useMemo(() => {
    const out = [];
    recent.forEach((r) => {
      const last = out[out.length - 1];
      if (last && last.day === r.day) last.rows.push(r);
      else out.push({ day: r.day, rows: [r] });
    });
    return out;
  }, [recent]);

  if (loading && !data) {
    return <div className="adm__card"><div className="adm-loading">Loading trading cards…</div></div>;
  }
  if (error && !data) return <div className="adm-error">{error}</div>;
  if (!data) return null;

  const w = data.window || {};
  const delta = (w.cards ?? 0) - (w.cards_prev ?? 0);
  const series = data.cards_per_day || [];
  const max = Math.max(1, ...series.map((s) => s.count));
  const dayCounts = Object.fromEntries((data.recent_day_counts || []).map((r) => [r.date, r.athletes]));
  const peak = data.peak_day;

  const templates = data.by_template_id || [];
  const templateTotal = templates.reduce((n, t) => n + t.count, 0);
  const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
  const templateSummary = templates.length === 0
    ? 'No cards yet'
    : templates.slice(0, 3).map((t) => `${titleCase(t.template_id)} ${pct(t.count, templateTotal)}%`).join(' · ')
      + (templates.length > 3 ? ` · ${templates.length - 3} more` : '');

  const f = data.ai_funnel || {};
  const funnelSummary = `${f.previews ?? 0} previews → ${f.generations ?? 0} generated → ${f.saves ?? 0} saved`
    + (f.generations ? ` (${pct(f.saves, f.generations)}%)` : '');
  const rate = (n, d) => (d ? `${((n / d) * 100).toFixed(1)}%` : '—');

  return (
    <div data-testid="trading-cards-report">
      {/* ---- stats + sparkline ---- */}
      <section className="adm__card tcr-stats" aria-label="Cards built">
        <div className="tcr-stats__nums">
          <div>
            <div className="adm-field__label">Cards built · {days} days</div>
            <div className="tcr-stats__headline">
              <span className="tcr-stats__value" data-testid="tc-window-cards">{w.cards ?? 0}</span>
              <span
                className={`tcr-delta${delta > 0 ? ' tcr-delta--up' : delta < 0 ? ' tcr-delta--down' : ''}`}
                title={`vs the previous ${days} days (${w.cards_prev ?? 0})`}
              >
                {delta > 0 ? `▲ ${delta}` : delta < 0 ? `▼ ${-delta}` : '±0'}
              </span>
            </div>
          </div>
          <div className="tcr-stats__row">
            {days === 7 ? (
              <div>
                <div className="adm-field__label">30 days</div>
                <div className="tcr-stats__sub">{w.cards_30d ?? 0}</div>
              </div>
            ) : (
              <div>
                <div className="adm-field__label">7 days</div>
                <div className="tcr-stats__sub">{w.cards_7d ?? 0}</div>
              </div>
            )}
            <div>
              <div className="adm-field__label">Athletes · 30d</div>
              <div className="tcr-stats__sub">{w.athletes_30d ?? 0}</div>
            </div>
            {data.ai_cards?.total > 0 && (
              <div title="Uploaded by staff, not built in the app; left out of every count above">
                <div className="adm-field__label">AI cards given</div>
                <div className="tcr-stats__sub" data-testid="tc-ai-cards">{data.ai_cards.total}</div>
              </div>
            )}
          </div>
        </div>

        <div className="tcr-spark">
          <div className="tcr-spark__caption">
            <span>Cards built per day · last {series.length} days</span>
            {peak && (
              <span data-testid="tc-peak">
                Peak {peak.count} · {fmtDay(peak.date, { month: 'short', day: 'numeric' })}
                {peak.camp_name ? ` (${peak.camp_name})` : ''}
              </span>
            )}
          </div>
          <div className={`tcr-spark__bars${series.length > 30 ? ' tcr-spark__bars--dense' : ''}`}>
            {series.map((s, i) => (
              <span
                key={s.date}
                data-testid="tc-bar"
                className={`tcr-spark__bar${i >= series.length - days ? ' tcr-spark__bar--in' : ''}`}
                style={{ height: `${Math.max(6, (s.count / max) * 100)}%` }}
                title={`${s.count} card${s.count === 1 ? '' : 's'} · ${fmtDay(s.date, { month: 'short', day: 'numeric' })}`}
              />
            ))}
          </div>
          <div className="tcr-spark__axis">
            <span>{series.length ? fmtDay(series[0].date, { month: 'short', day: 'numeric' }) : ''}</span>
            <span className="tcr-spark__axis-mid">last {days} days</span>
            <span>Today</span>
          </div>
        </div>
      </section>

      {/* ---- most recent card per athlete ---- */}
      <section className="adm__card tcr-recent">
        <div className="tcr-recent__head">
          <h2 className="tcr-recent__title">Most recent card per athlete</h2>
          <span className="tcr-recent__meta">
            Last {days} days · {w.athletes ?? recent.length} athlete{(w.athletes ?? recent.length) === 1 ? '' : 's'}
            {' · '}click a card to open full size
          </span>
        </div>

        {recent.length === 0 ? (
          <div className="adm-empty">No cards built in the last {days} days.</div>
        ) : (
          // One continuous grid, newest first, left to right (David, 2026-10-24): a row per day wasted a
          // screen of scroll on days with one card. The first card of each day carries the day marker.
          <div className="tcr-grid">
            {groups.flatMap((g) => {
              const label = dayLabel(g.day, today);
              const n = dayCounts[g.day] ?? g.rows.length;
              return g.rows.map((r, i) => (
                <CardTile key={r.user_id} row={r} today={today} tz={tz}
                  dayMarker={i === 0 ? label : null}
                  dayTitle={i === 0 ? `${n} athlete${n === 1 ? '' : 's'} built a card this day` : null} />
              ));
            })}
          </div>
        )}

        {error && data && <div className="adm-error" style={{ marginTop: 12 }}>{error}</div>}

        {cursor && (
          <div className="tcr-more">
            <button type="button" className="adm-btn" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Show earlier'}
            </button>
          </div>
        )}
      </section>

      {/* ---- collapsed rows ---- */}
      <section className="adm__card adm__card--flush">
        <div className="tcr-fold">
          <button
            type="button"
            className="tcr-fold__btn"
            aria-expanded={expanded.templates}
            onClick={() => toggle('templates')}
          >
            <span className="tcr-fold__label">By template</span>
            <span className="tcr-fold__summary">
              {templateSummary}<span className="tcr-fold__caret" aria-hidden="true">▾</span>
            </span>
          </button>
          {expanded.templates && (
            <div className="tcr-fold__body">
              <table className="tcr-table">
                <thead>
                  <tr><th>Template</th><th className="tcr-num">Count</th></tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.template_id ?? 'none'}>
                      <td>{t.template_id ?? '—'}</td>
                      <td className="tcr-num">{t.count}</td>
                    </tr>
                  ))}
                  {templates.length === 0 && <tr><td colSpan={2}>—</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="tcr-fold">
          <button
            type="button"
            className="tcr-fold__btn"
            aria-expanded={expanded.funnel}
            onClick={() => toggle('funnel')}
          >
            <span className="tcr-fold__label">AI card funnel</span>
            <span className="tcr-fold__summary">
              {funnelSummary}<span className="tcr-fold__caret" aria-hidden="true">▾</span>
            </span>
          </button>
          {expanded.funnel && (
            <div className="tcr-fold__body">
              <div className="tcr-tiles">
                <div className="tcr-tile">
                  <div className="adm-field__label">Previews</div>
                  <div className="tcr-tile__value">{f.previews ?? 0}</div>
                </div>
                <div className="tcr-tile">
                  <div className="adm-field__label">Generations</div>
                  <div className="tcr-tile__value">{f.generations ?? 0}</div>
                  <div className="tcr-tile__sub">{rate(f.generations, f.previews)} of previews</div>
                </div>
                <div className="tcr-tile">
                  <div className="adm-field__label">Saves</div>
                  <div className="tcr-tile__value">{f.saves ?? 0}</div>
                  <div className="tcr-tile__sub">{rate(f.saves, f.generations)} of generations</div>
                </div>
                <div className="tcr-tile">
                  <div className="adm-field__label">Collection views</div>
                  <div className="tcr-tile__value">{f.collection_views ?? 0}</div>
                </div>
                <div className="tcr-tile">
                  <div className="adm-field__label">Card detail views</div>
                  <div className="tcr-tile__value">{f.detail_views ?? 0}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function CardTile({ row, today, tz, dayMarker, dayTitle }) {
  const isNew = Date.now() - parseIso(row.created_at).getTime() < NEW_MS;
  const pos = [row.position, row.graduation_year].filter(Boolean).join(' · ');
  const tile = (
    <div className="tcr-card__tile" style={{ background: templateColor(row.template_id) }}>
      {row.composite_url
        ? <img className="tcr-card__img" src={row.composite_url} alt={`${row.name} trading card`} loading="lazy" />
        : <div className="tcr-card__initials" aria-hidden="true">{initials(row.name)}</div>}
      {/* A rendered card already shows the athlete, school and template, so the overlay is only for the
          placeholder; drawing it over the real image doubled the name and position. */}
      {!row.composite_url && (
        <>
          <div className="tcr-card__shade" />
          <div className="tcr-card__template">{row.template_id || 'card'}</div>
          <div className="tcr-card__who">
            <div className="tcr-card__name">{row.name}</div>
            {pos && <div className="tcr-card__pos">{pos}</div>}
          </div>
        </>
      )}
      {isNew && <span className="tcr-card__new">New</span>}
    </div>
  );
  const marker = (
    <div className={`tcr-card__day${dayMarker ? ' tcr-card__day--start' : ''}`} data-testid={dayMarker ? 'tc-day' : undefined}
      title={dayTitle || undefined}>
      {dayMarker || '\u00a0'}
    </div>
  );
  const foot = (
    <div className="tcr-card__foot">
      <span className="tcr-card__when">{whenLabel(row.created_at, row.day, today, tz)}</span>
      <span className="tcr-card__count">{row.card_count === 1 ? '1 card' : `${row.card_count} cards`}</span>
    </div>
  );
  if (!row.composite_url) {
    return (
      <div className="tcr-card" data-testid="tc-card" title="Not rendered yet — no full-size image">
        {marker}{tile}{foot}
      </div>
    );
  }
  return (
    <a
      className="tcr-card"
      data-testid="tc-card"
      href={row.composite_url}
      target="_blank"
      rel="noopener noreferrer"
      title="Open full-size card"
    >
      {marker}{tile}{foot}
    </a>
  );
}
